import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import FormModal from "../../../components/form/FormModal";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";
import FormTextarea from "../../../components/form/FormTextarea";
import FormCheckbox from "../../../components/form/FormCheckbox";
import {
  DEFAULT_WEBSITE_SECTION_ORDER,
  SELECTABLE_THEME_LAYOUTS,
  THEME_OPTIONS,
  WEBSITE_SECTION_KEYS,
  getThemeDefinition,
} from "../../PublicVenue/themeDefinitions";
import {
  BOX_ARENA_ARRAY_DEFINITIONS,
  BOX_ARENA_COPY_GROUPS,
  BOX_ARENA_MEDIA_FIELDS,
  BOX_ARENA_SOCIAL_FIELDS,
  PICKLE_POINT_ARRAY_DEFINITIONS,
  PICKLE_POINT_COPY_GROUPS,
  PICKLE_POINT_MEDIA_FIELDS,
  PICKLE_POINT_SOCIAL_FIELDS,
  MERLION_ARRAY_DEFINITIONS,
  MERLION_COPY_GROUPS,
  MERLION_MEDIA_FIELDS,
  MERLION_SOCIAL_FIELDS,
  BSA_ARRAY_DEFINITIONS,
  BSA_COPY_GROUPS,
  BSA_MEDIA_FIELDS,
  BSA_SOCIAL_FIELDS,
  TURF_PARK_ARRAY_DEFINITIONS,
  TURF_PARK_COPY_GROUPS,
  TURF_PARK_MEDIA_FIELDS,
  TURF_PARK_SOCIAL_FIELDS,
  BOXARENA_COURTLINE_ARRAY_DEFINITIONS,
  BOXARENA_COURTLINE_COPY_GROUPS,
  BOXARENA_COURTLINE_MEDIA_FIELDS,
  BOXARENA_COURTLINE_SOCIAL_FIELDS,
  COMMON_COPY_GROUPS,
  COMMON_MEDIA_FIELDS,
  getThemeContent,
  hasWebsiteConfigurationOverride,
  isWebsiteConfigurationFieldVisible,
  normalizeWebsiteExtensions,
  resolveThemeHeroValue,
  resolveThemeLogo,
  resolveThemePalette,
  resolveWebsiteConfigurationEditorValue,
} from "../../PublicVenue/publicWebsiteContent";
import {
  buildPublicVenueUrl,
  getPublicWebsiteSubdomainError,
  normalizePublicWebsiteSubdomain,
} from "../../PublicVenue/publicVenueHostname";
import WebsiteLivePreview from "./WebsiteLivePreview";
import "./VenueWebsiteBuilderModal.css";

const COMMON_BUILDER_COPY_GROUPS =
  COMMON_COPY_GROUPS.filter((group) =>
    ["payment-status", "booking-modal"].includes(
      group.id,
    ),
  );

const DEFAULT_SECTION_ORDER = DEFAULT_WEBSITE_SECTION_ORDER;


const ARENA_COPY_KEYS = [
  "hoursPrefix",
  "hoursSuffix",
  "heroBadge",
  "groundsEyebrow",
  "groundsTitle",
  "groundsDescription",
  "groundCapacitySuffix",
  "pricePrefix",
  "priceOnRequestLabel",
  "groundBookLabel",
  "emptyGroundsLabel",
  "bookingEyebrow",
  "bookingTitle",
  "bookingDescription",
  "bookingStepOneTitle",
  "bookingStepOneDescription",
  "bookingStepTwoTitle",
  "bookingStepTwoDescription",
  "bookingStepThreeTitle",
  "bookingStepThreeDescription",
  "bookingButtonLabel",
  "testimonialsEyebrow",
  "testimonialsTitle",
  "aboutEyebrow",
  "aboutTitle",
  "locationEyebrow",
  "locationContactLabel",
  "contactEyebrow",
  "contactTitle",
  "contactDescription",
  "contactOpenLabel",
  "contactOpenDescription",
  "faqEyebrow",
  "faqTitle",
  "footerQuickLinksTitle",
  "footerBookLabel",
  "footerAboutLabel",
  "footerContactLabel",
  "footerStartBookingLabel",
  "footerVisitTitle",
  "footerCopyrightSuffix",
  "footerPoweredByText",
];

const ARENA_COPY_FIELDS = {
  hoursPrefix: { label: "Hours prefix" },
  hoursSuffix: { label: "Hours suffix" },
  heroBadge: { label: "Hero badge" },

  groundsEyebrow: { label: "Grounds eyebrow" },
  groundsTitle: { label: "Grounds title" },
  groundsDescription: {
    label: "Grounds description",
    multiline: true,
  },
  groundCapacitySuffix: { label: "Ground capacity suffix" },
  pricePrefix: { label: "Price prefix" },
  priceOnRequestLabel: { label: "Price on request label" },
  groundBookLabel: { label: "Ground booking label" },
  emptyGroundsLabel: {
    label: "No grounds message",
    multiline: true,
  },

  bookingEyebrow: { label: "Booking eyebrow" },
  bookingTitle: { label: "Booking title" },
  bookingDescription: {
    label: "Booking description",
    multiline: true,
  },
  bookingStepOneTitle: { label: "Booking step 1 title" },
  bookingStepOneDescription: {
    label: "Booking step 1 description",
    multiline: true,
  },
  bookingStepTwoTitle: { label: "Booking step 2 title" },
  bookingStepTwoDescription: {
    label: "Booking step 2 description",
    multiline: true,
  },
  bookingStepThreeTitle: { label: "Booking step 3 title" },
  bookingStepThreeDescription: {
    label: "Booking step 3 description",
    multiline: true,
  },
  bookingButtonLabel: { label: "Booking section button" },

  testimonialsEyebrow: { label: "Testimonials eyebrow" },
  testimonialsTitle: { label: "Testimonials title" },

  aboutEyebrow: { label: "About eyebrow" },
  aboutTitle: { label: "About title" },

  locationEyebrow: { label: "Location eyebrow" },
  locationContactLabel: { label: "Location contact label" },

  contactEyebrow: { label: "Contact eyebrow" },
  contactTitle: { label: "Contact title" },
  contactDescription: {
    label: "Contact description",
    multiline: true,
  },
  contactOpenLabel: { label: "Contact opening-hours label" },
  contactOpenDescription: {
    label: "Contact opening-hours description",
    multiline: true,
  },

  faqEyebrow: { label: "FAQ eyebrow" },
  faqTitle: { label: "FAQ title" },

  footerQuickLinksTitle: { label: "Footer quick-links title" },
  footerBookLabel: { label: "Footer booking link" },
  footerAboutLabel: { label: "Footer about link" },
  footerContactLabel: { label: "Footer contact link" },
  footerStartBookingLabel: { label: "Footer booking button" },
  footerVisitTitle: { label: "Footer visit title" },
  footerCopyrightSuffix: { label: "Footer copyright suffix" },
  footerPoweredByText: { label: "Footer powered-by text" },
};

const ARENA_APP_FIELDS = {
  badge: { label: "App badge" },
  title: { label: "App title" },
  highlightedTitle: { label: "App highlighted title" },
  description: {
    label: "App description",
    multiline: true,
  },
  googlePlayUrl: {
    label: "Google Play URL",
    type: "url",
  },
  appStoreUrl: {
    label: "App Store URL",
    type: "url",
  },
  proofText: { label: "App social-proof text" },
  ctaLabel: { label: "App booking button" },
  greeting: { label: "Phone preview greeting" },
  customerName: { label: "Phone preview customer name" },
  featuredLabel: { label: "Phone featured-ground label" },
  featuredTime: { label: "Phone featured time" },
  slotsLabel: { label: "Phone slots label" },
  confirmLabel: { label: "Phone confirmation label" },
  ratingText: { label: "Floating rating text" },
  reviewsText: { label: "Floating reviews text" },
  instantTitle: { label: "Instant-confirm title" },
  instantSubtitle: { label: "Instant-confirm subtitle" },
};

const createDefaultArenaConfig = () => ({
  contactEmail: "",
  rating: 0,
  reviewCount: 0,

  copy: ARENA_COPY_KEYS.reduce((result, key) => {
    result[key] = "";
    return result;
  }, {}),

  testimonials: [],
  faqs: [],
  stats: [],

  social: {
    instagramUrl: "",
    youtubeUrl: "",
    facebookUrl: "",
  },

  app: {
    enabled: false,
    badge: "",
    title: "",
    highlightedTitle: "",
    description: "",
    features: [],
    googlePlayUrl: "",
    appStoreUrl: "",
    proofText: "",
    ctaLabel: "",
    greeting: "",
    customerName: "",
    featuredLabel: "",
    featuredTime: "",
    slotsLabel: "",
    confirmLabel: "",
    ratingText: "",
    reviewsText: "",
    instantTitle: "",
    instantSubtitle: "",
  },
});

const normalizeArenaConfig = (value) => {
  const defaults = createDefaultArenaConfig();

  const input =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : {};

  const copy =
    input.copy &&
      typeof input.copy === "object" &&
      !Array.isArray(input.copy)
      ? input.copy
      : {};

  const social =
    input.social &&
      typeof input.social === "object" &&
      !Array.isArray(input.social)
      ? input.social
      : {};

  const app =
    input.app &&
      typeof input.app === "object" &&
      !Array.isArray(input.app)
      ? input.app
      : {};

  return {
    ...defaults,
    ...input,

    copy: {
      ...defaults.copy,
      ...copy,
    },

    testimonials: Array.isArray(input.testimonials)
      ? input.testimonials
      : [],

    faqs: Array.isArray(input.faqs)
      ? input.faqs
      : [],

    stats: Array.isArray(input.stats)
      ? input.stats
      : [],

    social: {
      ...defaults.social,
      ...social,
    },

    app: {
      ...defaults.app,
      ...app,
      features: Array.isArray(app.features)
        ? app.features
        : [],
    },
  };
};

const DEFAULT_PUBLIC_WEBSITE = {
  subdomain: "",
  enabled: true,
  status: "published",
  theme: {
    layout: "arena",
    primaryColor: "#33A444",
    accentColor: "#111827",
    backgroundStyle: "clean",
  },
  hero: {
    headline: "",
    subheadline: "",
    ctaLabel: "Book Now",
  },

  copy: {},
  media: {},
  themeContent: {},

  arena: createDefaultArenaConfig(),

  sections: WEBSITE_SECTION_KEYS.reduce((sections, key) => {
    sections[key] = true;
    return sections;
  }, {}),
  sectionOrder: DEFAULT_SECTION_ORDER,
  booking: {
    enabled: true,
    slotSize: 60,
    requirePhone: true,
    requireEmail: false,
  },
  seo: {
    title: "",
    description: "",
    shareImageKey: "",
  },
};

const normalizeWebsite = (value) => {
  const input = value && typeof value === "object" ? value : {};
  const extensions = normalizeWebsiteExtensions(input);

  return {
    ...DEFAULT_PUBLIC_WEBSITE,
    ...input,
    ...extensions,
    theme: {
      ...DEFAULT_PUBLIC_WEBSITE.theme,
      ...(input.theme || {}),
      layout:
        SELECTABLE_THEME_LAYOUTS.includes(input.theme?.layout)
          ? input.theme.layout
          : DEFAULT_PUBLIC_WEBSITE.theme.layout,
    },
    hero: {
      ...DEFAULT_PUBLIC_WEBSITE.hero,
      ...(input.hero || {}),
    },

    arena: normalizeArenaConfig(input.arena),

    sections: {
      ...DEFAULT_PUBLIC_WEBSITE.sections,
      ...(input.sections || {}),
    },
    sectionOrder:
      Array.isArray(input.sectionOrder) && input.sectionOrder.length
        ? input.sectionOrder.filter((key) =>
          DEFAULT_SECTION_ORDER.includes(key),
        )
        : DEFAULT_SECTION_ORDER,
    booking: {
      ...DEFAULT_PUBLIC_WEBSITE.booking,
      ...(input.booking || {}),
      slotSize: 60,
    },
    seo: {
      ...DEFAULT_PUBLIC_WEBSITE.seo,
      ...(input.seo || {}),
    },
  };
};

const getThemePaletteForWebsite = (website, themeId) =>
  resolveThemePalette(
    website,
    themeId,
    getThemeDefinition(themeId).defaultPalette,
  );

const getThemeHeroForWebsite = (website, themeId) => ({
  headline: resolveThemeHeroValue(
    website,
    themeId,
    "headline",
    "",
  ),
  subheadline: resolveThemeHeroValue(
    website,
    themeId,
    "subheadline",
    "",
  ),
  ctaLabel: resolveThemeHeroValue(
    website,
    themeId,
    "ctaLabel",
    "Book Now",
  ),
});

const syncThemeScopedConfiguration = (
  website,
  themeId,
) => {
  const content = getThemeContent(website, themeId);
  const palette = getThemePaletteForWebsite(
    website,
    themeId,
  );
  const hero = getThemeHeroForWebsite(
    website,
    themeId,
  );
  const logoUrl = resolveThemeLogo(
    website,
    themeId,
    "",
  );

  const configuredPalette =
    content?.palette &&
    typeof content.palette === "object" &&
    !Array.isArray(content.palette)
      ? content.palette
      : {};
  const configuredHero =
    content?.hero &&
    typeof content.hero === "object" &&
    !Array.isArray(content.hero)
      ? content.hero
      : {};
  const configuredMedia =
    content?.media &&
    typeof content.media === "object" &&
    !Array.isArray(content.media)
      ? content.media
      : {};

  return {
    ...website,
    theme: {
      ...(website.theme || {}),
      ...palette,
    },
    hero: {
      ...(website.hero || {}),
      ...hero,
    },
    media: {
      ...(website.media || {}),
      logoUrl,
    },
    themeContent: {
      ...(website.themeContent || {}),
      [themeId]: {
        ...content,
        palette: {
          ...configuredPalette,
          ...palette,
        },
        hero: {
          ...configuredHero,
          ...hero,
        },
        media: {
          ...configuredMedia,
          logoUrl,
        },
      },
    },
  };
};

const ThemePreview = ({
  layout,
  primaryColor = "#33A444",
  accentColor = "#111827",
}) => (
  <div
    className={`vwb-theme-preview vwb-theme-preview--${layout}`}
    style={{
      "--vwb-preview-primary": primaryColor,
      "--vwb-preview-accent": accentColor,
    }}
    aria-hidden="true"
  >
    <div className="vwb-theme-preview-nav">
      <span className="vwb-theme-preview-logo" />
      <span />
      <span />
      <span />
      <i />
    </div>

    <div className="vwb-theme-preview-hero">
      <div className="vwb-theme-preview-copy">
        <span />
        <strong />
        <em />
      </div>

      <div className="vwb-theme-preview-media" />
    </div>

    <div className="vwb-theme-preview-cards">
      <span />
      <span />
      <span />
    </div>
  </div>
);

const WebsiteConfigurationField = ({
  field,
  value,
  effectiveValue,
  onChange,
}) => {
  const hasOverride =
    hasWebsiteConfigurationOverride(value);

  const inheritedValue =
    effectiveValue !== undefined
      ? effectiveValue
      : field.fallback;

  const editorValue =
    resolveWebsiteConfigurationEditorValue(
      value,
      inheritedValue,
    );

  const hasInheritedValue =
    hasWebsiteConfigurationOverride(inheritedValue);

  const valueStateHint = hasOverride
    ? hasInheritedValue
      ? `Customized. Clear this field to restore: ${inheritedValue}`
      : "Customized. Clear this field to restore the current venue or theme value."
    : hasInheritedValue
      ? "Showing the value currently used when no custom override is saved. Edit it to customize."
      : "No custom override is saved. The venue, live data, or bundled theme value remains in use until you enter one.";

  const fieldHint = [valueStateHint, field.hint]
    .filter(Boolean)
    .join(" ");

  const handleChange = (event) => {
    const rawValue = event.target.value;

    onChange(
      field.type === "number" && rawValue !== ""
        ? Number(rawValue)
        : rawValue,
    );
  };

  return (
    <FormField
      label={field.label}
      hint={fieldHint}
      className={field.multiline ? "venue-span-2" : ""}
    >
      {field.multiline ? (
        <FormTextarea
          rows={4}
          maxLength={field.maxLength}
          value={editorValue}
          placeholder="Enter a custom value"
          onChange={handleChange}
        />
      ) : (
        <FormInput
          type={field.type || "text"}
          min={field.key === "rating" ? "0" : undefined}
          max={field.key === "rating" ? "5" : undefined}
          step={field.key === "rating" ? "1" : undefined}
          maxLength={
            field.type === "number"
              ? undefined
              : field.maxLength
          }
          value={editorValue}
          placeholder="Enter a custom value"
          onChange={handleChange}
        />
      )}
    </FormField>
  );
};

const VenueWebsiteBuilderModal = ({
  open,
  venue,
  media,
  grounds = [],
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(DEFAULT_PUBLIC_WEBSITE);

  useEffect(() => {
    setForm(normalizeWebsite(venue?.publicWebsite));
  }, [venue, open]);

  const subdomainError = useMemo(
    () => getPublicWebsiteSubdomainError(form.subdomain),
    [form.subdomain],
  );

  const focusWebsiteField = (control) => {
    if (!control) return;

    control.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    window.requestAnimationFrame(() => {
      control.focus({ preventScroll: true });
    });
  };

  const focusSubdomainField = () => {
    focusWebsiteField(
      document.getElementById("venue-website-subdomain"),
    );
  };

  const getWebsiteFieldLabel = (control) =>
    control
      ?.closest(".fs-field")
      ?.querySelector(".fs-label")
      ?.textContent
      ?.trim() || "Please check this field";

  const publicUrl = useMemo(
    () =>
      buildPublicVenueUrl(
        {
          subdomain: form.subdomain,
          slug: venue?.slug,
        },
        window.location,
      ),
    [form.subdomain, venue?.slug],
  );

  const themeDefinition = useMemo(
    () =>
      getThemeDefinition(
        form.theme?.layout ||
          DEFAULT_PUBLIC_WEBSITE.theme.layout,
      ),
    [form.theme?.layout],
  );

  const themeControls = themeDefinition.controls;

  const selectedThemeLayout =
    form.theme?.layout ||
    DEFAULT_PUBLIC_WEBSITE.theme.layout;

  const selectedThemePalette = getThemePaletteForWebsite(
    form,
    selectedThemeLayout,
  );

  const selectedThemeLogo = resolveThemeLogo(
    form,
    selectedThemeLayout,
    "",
  );

  const mediaGallery = Array.isArray(media?.gallery)
    ? media.gallery
    : [];

  const venueGallery = Array.isArray(
    venue?.media?.gallery,
  )
    ? venue.media.gallery
    : [];

  const currentCommonHeroImage =
    media?.bannerUrl ||
    venue?.media?.bannerImage ||
    mediaGallery[0] ||
    venueGallery[0] ||
    "";

  const effectiveHeroHeadline =
    form.hero?.headline ||
    venue?.name ||
    form.copy?.heroHeadlineFallback ||
    "Book your favourite sports venue";

  const effectiveHeroSubheadline =
    form.hero?.subheadline ||
    venue?.shortDescription ||
    form.copy?.heroSubheadlineFallback ||
    "Explore facilities, available grounds, timings, pricing, and book your slot online.";

  const effectiveSeoTitle =
    form.seo?.title ||
    venue?.name ||
    form.copy?.seoTitleFallback ||
    "Venue Booking";

  const effectiveSeoDescription =
    form.seo?.description ||
    venue?.shortDescription ||
    form.copy?.seoDescriptionFallback ||
    "Book sports venues online.";

  const visibleCommonCopyGroups = useMemo(
    () =>
      COMMON_BUILDER_COPY_GROUPS.map((group) => ({
        ...group,
        fields: group.fields.filter((field) =>
          isWebsiteConfigurationFieldVisible(
            field,
            selectedThemeLayout,
          ),
        ),
      })).filter((group) => group.fields.length > 0),
    [selectedThemeLayout],
  );

  const visibleCommonMediaFields = useMemo(
    () =>
      COMMON_MEDIA_FIELDS.filter((field) =>
        isWebsiteConfigurationFieldVisible(
          field,
          selectedThemeLayout,
        ),
      ),
    [selectedThemeLayout],
  );

  const setRoot = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setNested = (group, key, value) => {
    setForm((prev) => ({
      ...prev,
      [group]: {
        ...(prev[group] || {}),
        [key]: value,
      },
    }));
  };

  const setConfigurationField = (group, key, value) => {
    setForm((prev) => ({
      ...prev,
      [group]: {
        ...(prev[group] || {}),
        [key]: value,
      },
    }));
  };

  const setThemeConfigurationField = (
    themeId,
    group,
    key,
    value,
  ) => {
    setForm((prev) => {
      const themeContent = {
        ...(prev.themeContent || {}),
      };

      const themeConfiguration = {
        ...(themeContent[themeId] || {}),
      };

      return {
        ...prev,
        themeContent: {
          ...themeContent,
          [themeId]: {
            ...themeConfiguration,
            [group]: {
              ...(themeConfiguration[group] || {}),
              [key]: value,
            },
          },
        },
      };
    });
  };

  const setThemeScopedField = (
    contentGroup,
    rootGroup,
    key,
    value,
  ) => {
    setForm((prev) => {
      const themeId =
        prev.theme?.layout ||
        DEFAULT_PUBLIC_WEBSITE.theme.layout;
      const themeContent = {
        ...(prev.themeContent || {}),
      };
      const themeConfiguration = {
        ...(themeContent[themeId] || {}),
      };
      const configuredGroup =
        themeConfiguration?.[contentGroup] &&
        typeof themeConfiguration[contentGroup] === "object" &&
        !Array.isArray(themeConfiguration[contentGroup])
          ? themeConfiguration[contentGroup]
          : {};

      return {
        ...prev,
        [rootGroup]: {
          ...(prev[rootGroup] || {}),
          [key]: value,
        },
        themeContent: {
          ...themeContent,
          [themeId]: {
            ...themeConfiguration,
            [contentGroup]: {
              ...configuredGroup,
              [key]: value,
            },
          },
        },
      };
    });
  };

  const selectTheme = (themeId) => {
    if (!SELECTABLE_THEME_LAYOUTS.includes(themeId)) {
      return;
    }

    setForm((prev) => {
      const currentThemeId =
        prev.theme?.layout ||
        DEFAULT_PUBLIC_WEBSITE.theme.layout;

      if (currentThemeId === themeId) {
        return prev;
      }

      const currentWebsite =
        syncThemeScopedConfiguration(
          prev,
          currentThemeId,
        );
      const nextPalette =
        getThemePaletteForWebsite(
          currentWebsite,
          themeId,
        );
      const nextHero =
        getThemeHeroForWebsite(
          currentWebsite,
          themeId,
        );
      const nextLogo = resolveThemeLogo(
        currentWebsite,
        themeId,
        "",
      );

      return {
        ...currentWebsite,
        theme: {
          ...(currentWebsite.theme || {}),
          layout: themeId,
          ...nextPalette,
        },
        hero: {
          ...(currentWebsite.hero || {}),
          ...nextHero,
        },
        media: {
          ...(currentWebsite.media || {}),
          logoUrl: nextLogo,
        },
      };
    });
  };

  const updateThemeArrayItem = (
    themeId,
    collection,
    index,
    key,
    value,
  ) => {
    setForm((prev) => {
      const themeContent = {
        ...(prev.themeContent || {}),
      };

      const themeConfiguration = {
        ...(themeContent[themeId] || {}),
      };

      const items = Array.isArray(
        themeConfiguration[collection],
      )
        ? [...themeConfiguration[collection]]
        : [];

      items[index] = {
        ...(items[index] || {}),
        [key]: value,
      };

      return {
        ...prev,
        themeContent: {
          ...themeContent,
          [themeId]: {
            ...themeConfiguration,
            [collection]: items,
          },
        },
      };
    });
  };

  const addThemeArrayItem = (
    themeId,
    collection,
    item,
  ) => {
    setForm((prev) => {
      const themeContent = {
        ...(prev.themeContent || {}),
      };

      const themeConfiguration = {
        ...(themeContent[themeId] || {}),
      };

      const items = Array.isArray(
        themeConfiguration[collection],
      )
        ? themeConfiguration[collection]
        : [];

      return {
        ...prev,
        themeContent: {
          ...themeContent,
          [themeId]: {
            ...themeConfiguration,
            [collection]: [
              ...items,
              { ...item },
            ],
          },
        },
      };
    });
  };

  const removeThemeArrayItem = (
    themeId,
    collection,
    index,
  ) => {
    setForm((prev) => {
      const themeContent = {
        ...(prev.themeContent || {}),
      };

      const themeConfiguration = {
        ...(themeContent[themeId] || {}),
      };

      const items = Array.isArray(
        themeConfiguration[collection],
      )
        ? themeConfiguration[collection]
        : [];

      return {
        ...prev,
        themeContent: {
          ...themeContent,
          [themeId]: {
            ...themeConfiguration,
            [collection]: items.filter(
              (_, itemIndex) => itemIndex !== index,
            ),
          },
        },
      };
    });
  };

  const setSection = (key, value) => {
    setNested("sections", key, value);
  };

  const setArenaField = (key, value) => {
    setForm((prev) => {
      const arena = normalizeArenaConfig(prev.arena);

      return {
        ...prev,
        arena: {
          ...arena,
          [key]: value,
        },
      };
    });
  };

  const setArenaNestedField = (group, key, value) => {
    setForm((prev) => {
      const arena = normalizeArenaConfig(prev.arena);

      return {
        ...prev,
        arena: {
          ...arena,
          [group]: {
            ...(arena[group] || {}),
            [key]: value,
          },
        },
      };
    });
  };

  const updateArenaArrayItem = (
    collection,
    index,
    key,
    value,
  ) => {
    setForm((prev) => {
      const arena = normalizeArenaConfig(prev.arena);
      const items = Array.isArray(arena[collection])
        ? [...arena[collection]]
        : [];

      items[index] = {
        ...(items[index] || {}),
        [key]: value,
      };

      return {
        ...prev,
        arena: {
          ...arena,
          [collection]: items,
        },
      };
    });
  };

  const addArenaArrayItem = (collection, item) => {
    setForm((prev) => {
      const arena = normalizeArenaConfig(prev.arena);
      const items = Array.isArray(arena[collection])
        ? arena[collection]
        : [];

      return {
        ...prev,
        arena: {
          ...arena,
          [collection]: [...items, item],
        },
      };
    });
  };

  const removeArenaArrayItem = (collection, index) => {
    setForm((prev) => {
      const arena = normalizeArenaConfig(prev.arena);
      const items = Array.isArray(arena[collection])
        ? arena[collection]
        : [];

      return {
        ...prev,
        arena: {
          ...arena,
          [collection]: items.filter(
            (_, itemIndex) => itemIndex !== index,
          ),
        },
      };
    });
  };

  const boxArenaContent = getThemeContent(
    form,
    "box-arena",
  );

  const picklePointContent = getThemeContent(
    form,
    "pickle-point-nepal",
  );

  const merlionContent = getThemeContent(
    form,
    "merlion-gymnasium",
  );

  const bhaisepatiContent = getThemeContent(
    form,
    "bhaisepati-sports-academy",
  );

  const turfParkContent = getThemeContent(
    form,
    "turf-park",
  );

  const boxArenaCourtlineContent = getThemeContent(
    form,
    "boxarena-courtline",
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (subdomainError) {
      toast.error(subdomainError, {
        toastId: "venue-website-subdomain-error",
      });
      focusSubdomainField();
      return;
    }

    const formElement = event.currentTarget;
    const firstInvalidControl = formElement?.querySelector?.(
      "input:invalid, select:invalid, textarea:invalid",
    );

    if (firstInvalidControl) {
      const label = getWebsiteFieldLabel(firstInvalidControl);
      const message =
        firstInvalidControl.validationMessage ||
        "Please check this value.";

      toast.error(`${label}: ${message}`, {
        toastId: "venue-website-field-error",
      });
      focusWebsiteField(firstInvalidControl);
      return;
    }

    const selectedThemeId =
      form.theme?.layout ||
      DEFAULT_PUBLIC_WEBSITE.theme.layout;
    const synchronizedWebsite =
      syncThemeScopedConfiguration(
        form,
        selectedThemeId,
      );
    const normalizedWebsite = normalizeWebsite(
      synchronizedWebsite,
    );
    const status =
      normalizedWebsite.enabled === false
        ? "unpublished"
        : normalizedWebsite.status || "published";

    try {
      await onSubmit?.({
        ...normalizedWebsite,
        enabled: status !== "unpublished",
        status,
      });
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        error?.message ||
        "";
      const serverField = String(
        error?.response?.data?.field ||
        error?.response?.data?.path ||
        "",
      ).toLowerCase();

      if (
        serverField.includes("subdomain") ||
        /subdomain/i.test(serverMessage)
      ) {
        focusSubdomainField();
      }
    }
  };

  return (
    <FormModal
      open={open}
      title="Configure Public Website"
      width="1480px"
      submitting={submitting}
      submitText="Save Website Settings"
      submittingText="Saving..."
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <div className="venue-form vwb-builder">
        <div className="vwb-builder-shell">
          <section
            className="vwb-settings-panel"
            aria-label="Website configuration"
          >
            <div className="venue-grid">
              <FormField
                label="Website Subdomain"
                error={subdomainError || undefined}
                hint="Set the branded address, for example boxarena. Leave blank to keep the legacy /venues/:slug URL. Changing it later changes the public link."
                className="venue-span-2"
              >
                <FormInput
                  id="venue-website-subdomain"
                  type="text"
                  maxLength={63}
                  value={form.subdomain || ""}
                  aria-invalid={!!subdomainError}
                  style={
                    subdomainError
                      ? {
                          borderColor: "#dc2626",
                          boxShadow:
                            "0 0 0 3px rgba(220, 38, 38, 0.08)",
                        }
                      : undefined
                  }
                  placeholder="boxarena"
                  onChange={(event) =>
                    setRoot(
                      "subdomain",
                      event.target.value.toLowerCase(),
                    )
                  }
                  onBlur={() =>
                    setRoot(
                      "subdomain",
                      normalizePublicWebsiteSubdomain(
                        form.subdomain,
                      ),
                    )
                  }
                />
              </FormField>

              <FormField
                label="Public Website URL"
                hint="This is the customer-facing booking website link."
                className="venue-span-2"
              >
                <FormInput
                  type="text"
                  value={publicUrl || "Not available yet"}
                  readOnly
                />
              </FormField>

              <FormField
                label="Publish Status"
                hint="Controls only the customer-facing venue website. Venue Active/Inactive remains a separate operational status."
              >
                <FormSelect
                  value={
                    form.enabled === false
                      ? "unpublished"
                      : form.status || "published"
                  }
                  onChange={(event) => {
                    const status = event.target.value;

                    setForm((prev) => ({
                      ...prev,
                      enabled: status !== "unpublished",
                      status,
                    }));
                  }}
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="unpublished">Unpublished</option>
                </FormSelect>
              </FormField>

              <FormField
                label="Website Theme"
                hint="Choose the complete customer-facing website layout."
                className="venue-span-2"
              >
                <div
                  className="vwb-theme-grid"
                  role="radiogroup"
                  aria-label="Public website theme"
                >
                  {THEME_OPTIONS.map((themeOption) => {
                    const selected =
                      (form.theme?.layout || "arena") === themeOption.id;
                    const optionPalette =
                      getThemePaletteForWebsite(
                        form,
                        themeOption.id,
                      );

                    return (
                      <button
                        key={themeOption.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`vwb-theme-card ${selected ? "is-selected" : ""
                          }`}
                        style={{
                          "--vwb-theme-primary":
                            optionPalette.primaryColor,
                        }}
                        onClick={() =>
                          selectTheme(themeOption.id)
                        }
                      >
                        <ThemePreview
                          layout={themeOption.id}
                          primaryColor={optionPalette.primaryColor}
                          accentColor={optionPalette.accentColor}
                        />

                        <span className="vwb-theme-card-content">
                          <span className="vwb-theme-card-heading">
                            <strong>{themeOption.name}</strong>

                            {selected && (
                              <span className="vwb-theme-selected">
                                ✓ Selected
                              </span>
                            )}
                          </span>

                          <span className="vwb-theme-description">
                            {themeOption.description}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </FormField>
              <FormField label="Primary Color">
                <FormInput
                  type="color"
                  value={selectedThemePalette.primaryColor}
                  onChange={(event) =>
                    setThemeScopedField(
                      "palette",
                      "theme",
                      "primaryColor",
                      event.target.value,
                    )
                  }
                />
              </FormField>

              <FormField label="Accent Color">
                <FormInput
                  type="color"
                  value={selectedThemePalette.accentColor}
                  onChange={(event) =>
                    setThemeScopedField(
                      "palette",
                      "theme",
                      "accentColor",
                      event.target.value,
                    )
                  }
                />
              </FormField>

              {themeControls.backgroundStyle && (
                <FormField
                  label="Background Style"
                  hint="Changes the Arena page background treatment."
                >
                  <FormSelect
                    value={selectedThemePalette.backgroundStyle}
                    onChange={(event) =>
                      setThemeScopedField(
                        "palette",
                        "theme",
                        "backgroundStyle",
                        event.target.value,
                      )
                    }
                  >
                    <option value="clean">Clean</option>
                    <option value="sport">Sport</option>
                    <option value="premium">Premium</option>
                  </FormSelect>
                </FormField>
              )}

              {themeControls.ctaLabel && (
                <FormField
                  label="CTA Label"
                  hint="Shows the value currently used. Editing it saves a custom override."
                >
                  <FormInput
                    type="text"
                    value={form.hero?.ctaLabel || "Book Now"}
                    onChange={(event) =>
                      setThemeScopedField(
                        "hero",
                        "hero",
                        "ctaLabel",
                        event.target.value,
                      )
                    }
                    placeholder="Book Now"
                  />
                </FormField>
              )}

              {themeControls.heroHeadline && (
                <FormField
                  label="Hero Headline"
                  hint="Shows the effective headline currently used by the selected theme. Editing it saves a custom override."
                  className="venue-span-2"
                >
                  <FormInput
                    type="text"
                    value={effectiveHeroHeadline}
                    onChange={(event) =>
                      setThemeScopedField(
                        "hero",
                        "hero",
                        "headline",
                        event.target.value,
                      )
                    }
                    placeholder={venue?.name || "Venue headline"}
                  />
                </FormField>
              )}

              {themeControls.heroSubheadline && (
                <FormField
                  label="Hero Subheadline"
                  hint="Shows the effective description currently used by the selected theme. Editing it saves a custom override."
                  className="venue-span-2"
                >
                  <FormTextarea
                    rows={3}
                    value={effectiveHeroSubheadline}
                    onChange={(event) =>
                      setThemeScopedField(
                        "hero",
                        "hero",
                        "subheadline",
                        event.target.value,
                      )
                    }
                    placeholder="Short customer-facing description for the venue website."
                  />
                </FormField>
              )}

              <FormField label="Booking Options">
                <div style={{ display: "grid", gap: 10 }}>
                  <FormCheckbox
                    checked={!!form.booking?.enabled}
                    onChange={(event) =>
                      setNested("booking", "enabled", event.target.checked)
                    }
                    label="Show booking widget"
                  />
                  <FormCheckbox
                    checked={!!form.booking?.requirePhone}
                    onChange={(event) =>
                      setNested("booking", "requirePhone", event.target.checked)
                    }
                    label="Require phone number"
                  />
                  <FormCheckbox
                    checked={!!form.booking?.requireEmail}
                    onChange={(event) =>
                      setNested("booking", "requireEmail", event.target.checked)
                    }
                    label="Require email address"
                  />
                </div>
              </FormField>

              {themeControls.sections && (
                <FormField
                  label="Website Sections"
                  hint={`Show or hide supported ${themeDefinition.name} page sections.`}
                  className="venue-span-2"
                >
                  <div
                    className="venue-tile-grid"
                    role="group"
                    aria-label="Website sections"
                  >
                    {themeDefinition.sectionOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className={`venue-tile ${form.sections?.[option.key] ? "is-selected" : ""}`}
                        onClick={() => setSection(option.key, !form.sections?.[option.key])}
                        aria-pressed={!!form.sections?.[option.key]}
                      >
                        <input
                          className="venue-tile-input"
                          type="checkbox"
                          checked={!!form.sections?.[option.key]}
                          onChange={() => setSection(option.key, !form.sections?.[option.key])}
                          tabIndex={-1}
                          aria-hidden="true"
                        />
                        <span className="venue-tile-label">
                          {option.label}
                        </span>
                        <span className="venue-tile-sub">
                          {form.sections?.[option.key] ? "Visible" : "Hidden"}
                        </span>
                      </button>
                    ))}
                  </div>
                </FormField>
              )}

              <div className="venue-span-2 vwb-arena-config">
                <div className="vwb-arena-heading">
                  <div>
                    <span>Shared booking content</span>
                    <h3>Booking and payment text</h3>
                  </div>

                  <p>
                    Every field shows its saved override or the exact
                    built-in value used when no override exists. Edit a
                    value to customize it; clearing it restores the
                    inherited value. These settings power the common
                    booking modal and payment-result pages for every
                    public theme.
                  </p>
                </div>

                {visibleCommonCopyGroups.map((group, groupIndex) => (
                  <details
                    className="vwb-arena-panel"
                    open={groupIndex === 0}
                    key={group.id}
                  >
                    <summary>{group.title}</summary>

                    <div className="vwb-arena-grid">
                      {group.fields.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={form.copy?.[field.key] || ""}
                          onChange={(value) =>
                            setConfigurationField(
                              "copy",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>
                ))}

                {visibleCommonMediaFields.length > 0 && (
                  <details className="vwb-arena-panel">
                    <summary>Selected theme media overrides</summary>

                    <div className="vwb-arena-grid">
                      {visibleCommonMediaFields.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            field.key === "logoUrl"
                              ? selectedThemeLogo
                              : form.media?.[field.key] || ""
                          }
                          effectiveValue={
                            field.key === "heroImageUrl"
                              ? currentCommonHeroImage
                              : undefined
                          }
                          onChange={(value) =>
                            field.key === "logoUrl"
                              ? setThemeScopedField(
                                  "media",
                                  "media",
                                  "logoUrl",
                                  value,
                                )
                              : setConfigurationField(
                                  "media",
                                  field.key,
                                  value,
                                )
                          }
                        />
                      ))}
                    </div>
                  </details>
                )}
              </div>

              {form.theme?.layout === "arena" && (
                <div className="venue-span-2 vwb-arena-config">
                  <div className="vwb-arena-heading">
                    <div>
                      <span>Arena theme</span>
                      <h3>Venue-specific Arena content</h3>
                    </div>

                    <p>
                      Website Configuration overrides take priority. Venue and
                      ground API data is used where applicable; blank fields and
                      empty lists keep the exact original Arena text and bundled
                      images. Disable a supported section to hide its fallback.
                    </p>
                  </div>

                  <details className="vwb-arena-panel" open>
                    <summary>General information</summary>

                    <div className="vwb-arena-grid">
                      <FormField label="Public contact email">
                        <FormInput
                          type="email"
                          value={form.arena?.contactEmail || ""}
                          onChange={(event) =>
                            setArenaField(
                              "contactEmail",
                              event.target.value,
                            )
                          }
                        />
                      </FormField>

                      <FormField
                        label="Public rating"
                        hint="Optional value from 0 to 5."
                      >
                        <FormInput
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                          value={form.arena?.rating ?? 0}
                          onChange={(event) =>
                            setArenaField(
                              "rating",
                              Number(event.target.value),
                            )
                          }
                        />
                      </FormField>

                      <FormField
                        label="Review count"
                        hint="Optional public review total."
                      >
                        <FormInput
                          type="number"
                          min="0"
                          step="1"
                          value={form.arena?.reviewCount ?? 0}
                          onChange={(event) =>
                            setArenaField(
                              "reviewCount",
                              Number(event.target.value),
                            )
                          }
                        />
                      </FormField>
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Page copy</summary>

                    <div className="vwb-arena-grid">
                      {Object.entries(ARENA_COPY_FIELDS).map(
                        ([key, field]) => (
                          <FormField
                            key={key}
                            label={field.label}
                            className={
                              field.multiline
                                ? "venue-span-2"
                                : ""
                            }
                          >
                            {field.multiline ? (
                              <FormTextarea
                                rows={3}
                                value={
                                  form.arena?.copy?.[key] || ""
                                }
                                onChange={(event) =>
                                  setArenaNestedField(
                                    "copy",
                                    key,
                                    event.target.value,
                                  )
                                }
                              />
                            ) : (
                              <FormInput
                                type="text"
                                value={
                                  form.arena?.copy?.[key] || ""
                                }
                                onChange={(event) =>
                                  setArenaNestedField(
                                    "copy",
                                    key,
                                    event.target.value,
                                  )
                                }
                              />
                            )}
                          </FormField>
                        ),
                      )}
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Testimonials</summary>

                    <div className="vwb-arena-list">
                      {(form.arena?.testimonials || []).map(
                        (testimonial, index) => (
                          <div
                            className="vwb-arena-item"
                            key={`testimonial-${index}`}
                          >
                            <div className="vwb-arena-item-heading">
                              <strong>
                                Testimonial {index + 1}
                              </strong>

                              <button
                                type="button"
                                className="vwb-arena-remove"
                                onClick={() =>
                                  removeArenaArrayItem(
                                    "testimonials",
                                    index,
                                  )
                                }
                              >
                                Remove
                              </button>
                            </div>

                            <div className="vwb-arena-grid">
                              <FormField label="Name">
                                <FormInput
                                  type="text"
                                  value={testimonial?.name || ""}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "testimonials",
                                      index,
                                      "name",
                                      event.target.value,
                                    )
                                  }
                                />
                              </FormField>

                              <FormField label="Role">
                                <FormInput
                                  type="text"
                                  value={testimonial?.role || ""}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "testimonials",
                                      index,
                                      "role",
                                      event.target.value,
                                    )
                                  }
                                />
                              </FormField>

                              <FormField label="Rating">
                                <FormInput
                                  type="number"
                                  min="0"
                                  max="5"
                                  step="1"
                                  value={testimonial?.rating ?? 0}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "testimonials",
                                      index,
                                      "rating",
                                      Number(event.target.value),
                                    )
                                  }
                                />
                              </FormField>

                              <FormField
                                label="Testimonial"
                                className="venue-span-2"
                              >
                                <FormTextarea
                                  rows={4}
                                  value={testimonial?.text || ""}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "testimonials",
                                      index,
                                      "text",
                                      event.target.value,
                                    )
                                  }
                                />
                              </FormField>
                            </div>
                          </div>
                        ),
                      )}

                      <button
                        type="button"
                        className="vwb-arena-add"
                        onClick={() =>
                          addArenaArrayItem("testimonials", {
                            name: "",
                            role: "",
                            text: "",
                            rating: 0,
                          })
                        }
                      >
                        Add testimonial
                      </button>
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Frequently asked questions</summary>

                    <div className="vwb-arena-list">
                      {(form.arena?.faqs || []).map(
                        (faq, index) => (
                          <div
                            className="vwb-arena-item"
                            key={`faq-${index}`}
                          >
                            <div className="vwb-arena-item-heading">
                              <strong>FAQ {index + 1}</strong>

                              <button
                                type="button"
                                className="vwb-arena-remove"
                                onClick={() =>
                                  removeArenaArrayItem(
                                    "faqs",
                                    index,
                                  )
                                }
                              >
                                Remove
                              </button>
                            </div>

                            <div className="vwb-arena-grid">
                              <FormField
                                label="Question"
                                className="venue-span-2"
                              >
                                <FormInput
                                  type="text"
                                  value={faq?.question || ""}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "faqs",
                                      index,
                                      "question",
                                      event.target.value,
                                    )
                                  }
                                />
                              </FormField>

                              <FormField
                                label="Answer"
                                className="venue-span-2"
                              >
                                <FormTextarea
                                  rows={4}
                                  value={faq?.answer || ""}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "faqs",
                                      index,
                                      "answer",
                                      event.target.value,
                                    )
                                  }
                                />
                              </FormField>
                            </div>
                          </div>
                        ),
                      )}

                      <button
                        type="button"
                        className="vwb-arena-add"
                        onClick={() =>
                          addArenaArrayItem("faqs", {
                            question: "",
                            answer: "",
                          })
                        }
                      >
                        Add FAQ
                      </button>
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Statistics</summary>

                    <div className="vwb-arena-list">
                      {(form.arena?.stats || []).map(
                        (stat, index) => (
                          <div
                            className="vwb-arena-item"
                            key={`stat-${index}`}
                          >
                            <div className="vwb-arena-item-heading">
                              <strong>Statistic {index + 1}</strong>

                              <button
                                type="button"
                                className="vwb-arena-remove"
                                onClick={() =>
                                  removeArenaArrayItem(
                                    "stats",
                                    index,
                                  )
                                }
                              >
                                Remove
                              </button>
                            </div>

                            <div className="vwb-arena-grid">
                              <FormField label="Value">
                                <FormInput
                                  type="text"
                                  value={stat?.value || ""}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "stats",
                                      index,
                                      "value",
                                      event.target.value,
                                    )
                                  }
                                />
                              </FormField>

                              <FormField label="Label">
                                <FormInput
                                  type="text"
                                  value={stat?.label || ""}
                                  onChange={(event) =>
                                    updateArenaArrayItem(
                                      "stats",
                                      index,
                                      "label",
                                      event.target.value,
                                    )
                                  }
                                />
                              </FormField>
                            </div>
                          </div>
                        ),
                      )}

                      <button
                        type="button"
                        className="vwb-arena-add"
                        onClick={() =>
                          addArenaArrayItem("stats", {
                            value: "",
                            label: "",
                          })
                        }
                      >
                        Add statistic
                      </button>
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Social links</summary>

                    <div className="vwb-arena-grid">
                      <FormField label="Instagram URL">
                        <FormInput
                          type="url"
                          value={
                            form.arena?.social?.instagramUrl || ""
                          }
                          onChange={(event) =>
                            setArenaNestedField(
                              "social",
                              "instagramUrl",
                              event.target.value,
                            )
                          }
                        />
                      </FormField>

                      <FormField label="YouTube URL">
                        <FormInput
                          type="url"
                          value={
                            form.arena?.social?.youtubeUrl || ""
                          }
                          onChange={(event) =>
                            setArenaNestedField(
                              "social",
                              "youtubeUrl",
                              event.target.value,
                            )
                          }
                        />
                      </FormField>

                      <FormField label="Facebook URL">
                        <FormInput
                          type="url"
                          value={
                            form.arena?.social?.facebookUrl || ""
                          }
                          onChange={(event) =>
                            setArenaNestedField(
                              "social",
                              "facebookUrl",
                              event.target.value,
                            )
                          }
                        />
                      </FormField>
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>App promotion</summary>

                    <div className="vwb-arena-grid">
                      <FormField
                        label="Show app promotion"
                        className="venue-span-2"
                      >
                        <FormCheckbox
                          checked={!!form.arena?.app?.enabled}
                          onChange={(event) =>
                            setArenaNestedField(
                              "app",
                              "enabled",
                              event.target.checked,
                            )
                          }
                          label="Display the Arena app-promotion section"
                        />
                      </FormField>

                      {form.arena?.app?.enabled && (
                        <>
                          {Object.entries(ARENA_APP_FIELDS).map(
                            ([key, field]) => (
                              <FormField
                                key={key}
                                label={field.label}
                                className={
                                  field.multiline
                                    ? "venue-span-2"
                                    : ""
                                }
                              >
                                {field.multiline ? (
                                  <FormTextarea
                                    rows={4}
                                    value={
                                      form.arena?.app?.[key] || ""
                                    }
                                    onChange={(event) =>
                                      setArenaNestedField(
                                        "app",
                                        key,
                                        event.target.value,
                                      )
                                    }
                                  />
                                ) : (
                                  <FormInput
                                    type={field.type || "text"}
                                    value={
                                      form.arena?.app?.[key] || ""
                                    }
                                    onChange={(event) =>
                                      setArenaNestedField(
                                        "app",
                                        key,
                                        event.target.value,
                                      )
                                    }
                                  />
                                )}
                              </FormField>
                            ),
                          )}

                          <FormField
                            label="App features"
                            hint="Enter one feature per line."
                            className="venue-span-2"
                          >
                            <FormTextarea
                              rows={6}
                              value={
                                (
                                  form.arena?.app?.features || []
                                ).join("\n")
                              }
                              onChange={(event) =>
                                setArenaNestedField(
                                  "app",
                                  "features",
                                  event.target.value.split(/\r?\n/),
                                )
                              }
                            />
                          </FormField>
                        </>
                      )}
                    </div>
                  </details>
                </div>
              )}


              {form.theme?.layout === "box-arena" && (
                <div className="venue-span-2 vwb-arena-config">
                  <div className="vwb-arena-heading">
                    <div>
                      <span>Box Arena theme</span>
                      <h3>Venue-specific Box Arena content</h3>
                    </div>

                    <p>
                      Fields show saved overrides or their exact built-in
                      fallbacks. Venue/API-backed values still take priority
                      where a field is explicitly labelled as a fallback.
                      Content saved for other themes remains untouched when
                      the selected theme changes.
                    </p>
                  </div>

                  {BOX_ARENA_COPY_GROUPS.map(
                    (group, groupIndex) => (
                      <details
                        className="vwb-arena-panel"
                        open={groupIndex === 0}
                        key={group.id}
                      >
                        <summary>{group.title}</summary>

                        <div className="vwb-arena-grid">
                          {group.fields.map((field) => (
                            <WebsiteConfigurationField
                              key={field.key}
                              field={field}
                              value={
                                boxArenaContent?.copy?.[
                                  field.key
                                ] || ""
                              }
                              onChange={(value) =>
                                setThemeConfigurationField(
                                  "box-arena",
                                  "copy",
                                  field.key,
                                  value,
                                )
                              }
                            />
                          ))}
                        </div>
                      </details>
                    ),
                  )}

                  <details className="vwb-arena-panel">
                    <summary>Theme media overrides</summary>

                    <div className="vwb-arena-grid">
                      {BOX_ARENA_MEDIA_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            boxArenaContent?.media?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "box-arena",
                              "media",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Social and store links</summary>

                    <div className="vwb-arena-grid">
                      {BOX_ARENA_SOCIAL_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            boxArenaContent?.social?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "box-arena",
                              "social",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  {Object.entries(
                    BOX_ARENA_ARRAY_DEFINITIONS,
                  ).map(([collection, definition]) => {
                    const items = Array.isArray(
                      boxArenaContent?.[collection],
                    )
                      ? boxArenaContent[collection]
                      : [];

                    return (
                      <details
                        className="vwb-arena-panel"
                        key={collection}
                      >
                        <summary>{definition.title}</summary>

                        <div className="vwb-arena-list">
                          {items.map((item, index) => (
                            <div
                              className="vwb-arena-item"
                              key={`${collection}-${index}`}
                            >
                              <div className="vwb-arena-item-heading">
                                <strong>
                                  {definition.title} {index + 1}
                                </strong>

                                <button
                                  type="button"
                                  className="vwb-arena-remove"
                                  onClick={() =>
                                    removeThemeArrayItem(
                                      "box-arena",
                                      collection,
                                      index,
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </div>

                              <div className="vwb-arena-grid">
                                {definition.fields.map((field) => (
                                  <WebsiteConfigurationField
                                    key={field.key}
                                    field={field}
                                    value={item?.[field.key] ?? ""}
                                    onChange={(value) =>
                                      updateThemeArrayItem(
                                        "box-arena",
                                        collection,
                                        index,
                                        field.key,
                                        value,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="vwb-arena-add"
                            onClick={() =>
                              addThemeArrayItem(
                                "box-arena",
                                collection,
                                definition.emptyItem,
                              )
                            }
                          >
                            {definition.addLabel}
                          </button>
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}


              {form.theme?.layout === "pickle-point-nepal" && (
                <div className="venue-span-2 vwb-arena-config">
                  <div className="vwb-arena-heading">
                    <div>
                      <span>Pickle Point Nepal theme</span>
                      <h3>Venue-specific Pickle Point content</h3>
                    </div>

                    <p>
                      Fields show saved overrides or their exact built-in
                      fallbacks. Venue and ground API data still take priority
                      where applicable. Content saved for other themes remains
                      untouched.
                    </p>
                  </div>

                  {PICKLE_POINT_COPY_GROUPS.map(
                    (group, groupIndex) => (
                      <details
                        className="vwb-arena-panel"
                        open={groupIndex === 0}
                        key={group.id}
                      >
                        <summary>{group.title}</summary>

                        <div className="vwb-arena-grid">
                          {group.fields.map((field) => (
                            <WebsiteConfigurationField
                              key={field.key}
                              field={field}
                              value={
                                picklePointContent?.copy?.[
                                  field.key
                                ] || ""
                              }
                              onChange={(value) =>
                                setThemeConfigurationField(
                                  "pickle-point-nepal",
                                  "copy",
                                  field.key,
                                  value,
                                )
                              }
                            />
                          ))}
                        </div>
                      </details>
                    ),
                  )}

                  <details className="vwb-arena-panel">
                    <summary>Theme media overrides</summary>

                    <div className="vwb-arena-grid">
                      {PICKLE_POINT_MEDIA_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            picklePointContent?.media?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "pickle-point-nepal",
                              "media",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Social, store and map links</summary>

                    <div className="vwb-arena-grid">
                      {PICKLE_POINT_SOCIAL_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            picklePointContent?.social?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "pickle-point-nepal",
                              "social",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  {Object.entries(
                    PICKLE_POINT_ARRAY_DEFINITIONS,
                  ).map(([collection, definition]) => {
                    const items = Array.isArray(
                      picklePointContent?.[collection],
                    )
                      ? picklePointContent[collection]
                      : [];

                    return (
                      <details
                        className="vwb-arena-panel"
                        key={collection}
                      >
                        <summary>{definition.title}</summary>

                        <div className="vwb-arena-list">
                          {items.map((item, index) => (
                            <div
                              className="vwb-arena-item"
                              key={`${collection}-${index}`}
                            >
                              <div className="vwb-arena-item-heading">
                                <strong>
                                  {definition.title} {index + 1}
                                </strong>

                                <button
                                  type="button"
                                  className="vwb-arena-remove"
                                  onClick={() =>
                                    removeThemeArrayItem(
                                      "pickle-point-nepal",
                                      collection,
                                      index,
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </div>

                              <div className="vwb-arena-grid">
                                {definition.fields.map((field) => (
                                  <WebsiteConfigurationField
                                    key={field.key}
                                    field={field}
                                    value={item?.[field.key] ?? ""}
                                    onChange={(value) =>
                                      updateThemeArrayItem(
                                        "pickle-point-nepal",
                                        collection,
                                        index,
                                        field.key,
                                        value,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="vwb-arena-add"
                            onClick={() =>
                              addThemeArrayItem(
                                "pickle-point-nepal",
                                collection,
                                definition.emptyItem,
                              )
                            }
                          >
                            {definition.addLabel}
                          </button>
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}


              {form.theme?.layout === "merlion-gymnasium" && (
                <div className="venue-span-2 vwb-arena-config">
                  <div className="vwb-arena-heading">
                    <div>
                      <span>Merlion Gymnasium theme</span>
                      <h3>Venue-specific Merlion content</h3>
                    </div>

                    <p>
                      Venue name, location, phone, opening hours,
                      facilities, grounds, prices, currency and map location
                      use the venue API and are intentionally not duplicated
                      here. Fields show saved overrides or their exact built-in
                      fallbacks; API data still takes priority where applicable.
                      The map field remains an optional embed override.
                    </p>
                  </div>

                  {MERLION_COPY_GROUPS.map(
                    (group, groupIndex) => (
                      <details
                        className="vwb-arena-panel"
                        open={groupIndex === 0}
                        key={group.id}
                      >
                        <summary>{group.title}</summary>

                        <div className="vwb-arena-grid">
                          {group.fields.map((field) => (
                            <WebsiteConfigurationField
                              key={field.key}
                              field={field}
                              value={
                                merlionContent?.copy?.[
                                  field.key
                                ] || ""
                              }
                              onChange={(value) =>
                                setThemeConfigurationField(
                                  "merlion-gymnasium",
                                  "copy",
                                  field.key,
                                  value,
                                )
                              }
                            />
                          ))}
                        </div>
                      </details>
                    ),
                  )}

                  <details className="vwb-arena-panel">
                    <summary>Theme media overrides</summary>

                    <div className="vwb-arena-grid">
                      {MERLION_MEDIA_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            merlionContent?.media?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "merlion-gymnasium",
                              "media",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Social, store and map links</summary>

                    <div className="vwb-arena-grid">
                      {MERLION_SOCIAL_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            merlionContent?.social?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "merlion-gymnasium",
                              "social",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  {Object.entries(
                    MERLION_ARRAY_DEFINITIONS,
                  ).map(([collection, definition]) => {
                    const items = Array.isArray(
                      merlionContent?.[collection],
                    )
                      ? merlionContent[collection]
                      : [];

                    return (
                      <details
                        className="vwb-arena-panel"
                        key={collection}
                      >
                        <summary>{definition.title}</summary>

                        <div className="vwb-arena-list">
                          {items.map((item, index) => (
                            <div
                              className="vwb-arena-item"
                              key={`${collection}-${index}`}
                            >
                              <div className="vwb-arena-item-heading">
                                <strong>
                                  {definition.title} {index + 1}
                                </strong>

                                <button
                                  type="button"
                                  className="vwb-arena-remove"
                                  onClick={() =>
                                    removeThemeArrayItem(
                                      "merlion-gymnasium",
                                      collection,
                                      index,
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </div>

                              <div className="vwb-arena-grid">
                                {definition.fields.map((field) => (
                                  <WebsiteConfigurationField
                                    key={field.key}
                                    field={field}
                                    value={item?.[field.key] ?? ""}
                                    onChange={(value) =>
                                      updateThemeArrayItem(
                                        "merlion-gymnasium",
                                        collection,
                                        index,
                                        field.key,
                                        value,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="vwb-arena-add"
                            onClick={() =>
                              addThemeArrayItem(
                                "merlion-gymnasium",
                                collection,
                                definition.emptyItem,
                              )
                            }
                          >
                            {definition.addLabel}
                          </button>
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}


              {form.theme?.layout === "bhaisepati-sports-academy" && (
                <div className="venue-span-2 vwb-arena-config">
                  <div className="vwb-arena-heading">
                    <div>
                      <span>Bhaisepati Sports Academy theme</span>
                      <h3>Venue-specific Bhaisepati content</h3>
                    </div>

                    <p>
                      Venue name, location, phone, opening hours,
                      facilities, grounds, prices, currency and map location
                      use the venue API and are intentionally not duplicated
                      here. Fields show saved overrides or their exact built-in
                      fallbacks; API data still takes priority where applicable.
                      The map field remains an optional embed override.
                    </p>
                  </div>

                  {BSA_COPY_GROUPS.map(
                    (group, groupIndex) => (
                      <details
                        className="vwb-arena-panel"
                        open={groupIndex === 0}
                        key={group.id}
                      >
                        <summary>{group.title}</summary>

                        <div className="vwb-arena-grid">
                          {group.fields.map((field) => (
                            <WebsiteConfigurationField
                              key={field.key}
                              field={field}
                              value={
                                bhaisepatiContent?.copy?.[
                                  field.key
                                ] || ""
                              }
                              onChange={(value) =>
                                setThemeConfigurationField(
                                  "bhaisepati-sports-academy",
                                  "copy",
                                  field.key,
                                  value,
                                )
                              }
                            />
                          ))}
                        </div>
                      </details>
                    ),
                  )}

                  <details className="vwb-arena-panel">
                    <summary>Theme media overrides</summary>

                    <div className="vwb-arena-grid">
                      {BSA_MEDIA_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            bhaisepatiContent?.media?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "bhaisepati-sports-academy",
                              "media",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Social, store and map links</summary>

                    <div className="vwb-arena-grid">
                      {BSA_SOCIAL_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            bhaisepatiContent?.social?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "bhaisepati-sports-academy",
                              "social",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  {Object.entries(
                    BSA_ARRAY_DEFINITIONS,
                  ).map(([collection, definition]) => {
                    const items = Array.isArray(
                      bhaisepatiContent?.[collection],
                    )
                      ? bhaisepatiContent[collection]
                      : [];

                    return (
                      <details
                        className="vwb-arena-panel"
                        key={collection}
                      >
                        <summary>{definition.title}</summary>

                        <div className="vwb-arena-list">
                          {items.map((item, index) => (
                            <div
                              className="vwb-arena-item"
                              key={`${collection}-${index}`}
                            >
                              <div className="vwb-arena-item-heading">
                                <strong>
                                  {definition.title} {index + 1}
                                </strong>

                                <button
                                  type="button"
                                  className="vwb-arena-remove"
                                  onClick={() =>
                                    removeThemeArrayItem(
                                      "bhaisepati-sports-academy",
                                      collection,
                                      index,
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </div>

                              <div className="vwb-arena-grid">
                                {definition.fields.map((field) => (
                                  <WebsiteConfigurationField
                                    key={field.key}
                                    field={field}
                                    value={item?.[field.key] ?? ""}
                                    onChange={(value) =>
                                      updateThemeArrayItem(
                                        "bhaisepati-sports-academy",
                                        collection,
                                        index,
                                        field.key,
                                        value,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="vwb-arena-add"
                            onClick={() =>
                              addThemeArrayItem(
                                "bhaisepati-sports-academy",
                                collection,
                                definition.emptyItem,
                              )
                            }
                          >
                            {definition.addLabel}
                          </button>
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}


              {form.theme?.layout === "turf-park" && (
                <div className="venue-span-2 vwb-arena-config">
                  <div className="vwb-arena-heading">
                    <div>
                      <span>Turf Park theme</span>
                      <h3>Venue-specific Turf Park content</h3>
                    </div>

                    <p>
                      Venue name, location, phone, opening hours,
                      facilities, grounds, prices, currency, statistics
                      and map location use the venue API and are
                      intentionally not duplicated here. Fields show saved
                      overrides or their exact built-in fallbacks; API data
                      still takes priority where applicable. The rating and
                      map behaviour remain unchanged.
                    </p>
                  </div>

                  {TURF_PARK_COPY_GROUPS.map(
                    (group, groupIndex) => (
                      <details
                        className="vwb-arena-panel"
                        open={groupIndex === 0}
                        key={group.id}
                      >
                        <summary>{group.title}</summary>

                        <div className="vwb-arena-grid">
                          {group.fields.map((field) => (
                            <WebsiteConfigurationField
                              key={field.key}
                              field={field}
                              value={
                                turfParkContent?.copy?.[
                                  field.key
                                ] || ""
                              }
                              onChange={(value) =>
                                setThemeConfigurationField(
                                  "turf-park",
                                  "copy",
                                  field.key,
                                  value,
                                )
                              }
                            />
                          ))}
                        </div>
                      </details>
                    ),
                  )}

                  <details className="vwb-arena-panel">
                    <summary>Theme media overrides</summary>

                    <div className="vwb-arena-grid">
                      {TURF_PARK_MEDIA_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            turfParkContent?.media?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "turf-park",
                              "media",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>App, social and map links</summary>

                    <div className="vwb-arena-grid">
                      {TURF_PARK_SOCIAL_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={
                            turfParkContent?.social?.[
                              field.key
                            ] || ""
                          }
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "turf-park",
                              "social",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  {Object.entries(
                    TURF_PARK_ARRAY_DEFINITIONS,
                  ).map(([collection, definition]) => {
                    const items = Array.isArray(
                      turfParkContent?.[collection],
                    )
                      ? turfParkContent[collection]
                      : [];

                    return (
                      <details
                        className="vwb-arena-panel"
                        key={collection}
                      >
                        <summary>{definition.title}</summary>

                        <div className="vwb-arena-list">
                          {items.map((item, index) => (
                            <div
                              className="vwb-arena-item"
                              key={`${collection}-${index}`}
                            >
                              <div className="vwb-arena-item-heading">
                                <strong>
                                  {definition.title} {index + 1}
                                </strong>

                                <button
                                  type="button"
                                  className="vwb-arena-remove"
                                  onClick={() =>
                                    removeThemeArrayItem(
                                      "turf-park",
                                      collection,
                                      index,
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </div>

                              <div className="vwb-arena-grid">
                                {definition.fields.map((field) => (
                                  <WebsiteConfigurationField
                                    key={field.key}
                                    field={field}
                                    value={item?.[field.key] ?? ""}
                                    onChange={(value) =>
                                      updateThemeArrayItem(
                                        "turf-park",
                                        collection,
                                        index,
                                        field.key,
                                        value,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="vwb-arena-add"
                            onClick={() =>
                              addThemeArrayItem(
                                "turf-park",
                                collection,
                                definition.emptyItem,
                              )
                            }
                          >
                            {definition.addLabel}
                          </button>
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}


              {form.theme?.layout === "boxarena-courtline" && (
                <div className="venue-span-2 vwb-arena-config">
                  <div className="vwb-arena-heading">
                    <div>
                      <span>BoxArena Courtline theme</span>
                      <h3>Venue-specific BoxArena Courtline content</h3>
                    </div>

                    <p>
                      Venue name, address, phone, opening hours, grounds,
                      sports, gallery media, facilities and map location
                      use the venue API and are intentionally not duplicated
                      here. Fields show saved overrides or their exact built-in
                      fallbacks; API data still takes priority where applicable.
                      Content saved for every other theme remains untouched.
                    </p>
                  </div>

                  {BOXARENA_COURTLINE_COPY_GROUPS.map((group, groupIndex) => (
                    <details
                      className="vwb-arena-panel"
                      open={groupIndex === 0}
                      key={group.id}
                    >
                      <summary>{group.title}</summary>
                      <div className="vwb-arena-grid">
                        {group.fields.map((field) => (
                          <WebsiteConfigurationField
                            key={field.key}
                            field={field}
                            value={boxArenaCourtlineContent?.copy?.[field.key] || ""}
                            onChange={(value) =>
                              setThemeConfigurationField(
                                "boxarena-courtline",
                                "copy",
                                field.key,
                                value,
                              )
                            }
                          />
                        ))}
                      </div>
                    </details>
                  ))}

                  <details className="vwb-arena-panel">
                    <summary>Theme media overrides</summary>
                    <div className="vwb-arena-grid">
                      {BOXARENA_COURTLINE_MEDIA_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={boxArenaCourtlineContent?.media?.[field.key] || ""}
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "boxarena-courtline",
                              "media",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  <details className="vwb-arena-panel">
                    <summary>Contact and map overrides</summary>
                    <div className="vwb-arena-grid">
                      {BOXARENA_COURTLINE_SOCIAL_FIELDS.map((field) => (
                        <WebsiteConfigurationField
                          key={field.key}
                          field={field}
                          value={boxArenaCourtlineContent?.social?.[field.key] || ""}
                          onChange={(value) =>
                            setThemeConfigurationField(
                              "boxarena-courtline",
                              "social",
                              field.key,
                              value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </details>

                  {Object.entries(BOXARENA_COURTLINE_ARRAY_DEFINITIONS).map(
                    ([collection, definition]) => {
                      const items = Array.isArray(
                        boxArenaCourtlineContent?.[collection],
                      )
                        ? boxArenaCourtlineContent[collection]
                        : [];

                      return (
                        <details className="vwb-arena-panel" key={collection}>
                          <summary>{definition.title}</summary>
                          <div className="vwb-arena-list">
                            {items.map((item, index) => (
                              <div
                                className="vwb-arena-item"
                                key={`${collection}-${index}`}
                              >
                                <div className="vwb-arena-item-heading">
                                  <strong>{definition.title} {index + 1}</strong>
                                  <button
                                    type="button"
                                    className="vwb-arena-remove"
                                    onClick={() =>
                                      removeThemeArrayItem(
                                        "boxarena-courtline",
                                        collection,
                                        index,
                                      )
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>

                                <div className="vwb-arena-grid">
                                  {definition.fields.map((field) => (
                                    <WebsiteConfigurationField
                                      key={field.key}
                                      field={field}
                                      value={item?.[field.key] ?? ""}
                                      onChange={(value) =>
                                        updateThemeArrayItem(
                                          "boxarena-courtline",
                                          collection,
                                          index,
                                          field.key,
                                          value,
                                        )
                                      }
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              className="vwb-arena-add"
                              onClick={() =>
                                addThemeArrayItem(
                                  "boxarena-courtline",
                                  collection,
                                  definition.emptyItem,
                                )
                              }
                            >
                              {definition.addLabel}
                            </button>
                          </div>
                        </details>
                      );
                    },
                  )}
                </div>
              )}


              <FormField
                label="SEO Title"
                hint="Shows the effective browser and search title currently used. Editing it saves a custom override."
                className="venue-span-2"
              >
                <FormInput
                  type="text"
                  value={effectiveSeoTitle}
                  onChange={(event) =>
                    setNested("seo", "title", event.target.value)
                  }
                  placeholder={venue?.name || "Venue page title"}
                />
              </FormField>

              <FormField
                label="SEO Description"
                hint="Shows the effective search description currently used. Editing it saves a custom override."
                className="venue-span-2"
              >
                <FormTextarea
                  rows={3}
                  value={effectiveSeoDescription}
                  onChange={(event) =>
                    setNested("seo", "description", event.target.value)
                  }
                  placeholder="Short search/social description for the venue website."
                />
              </FormField>
            </div>
          </section>

          <WebsiteLivePreview
            venue={venue}
            media={media}
            grounds={grounds}
            website={form}
            publicUrl={publicUrl}
          />
        </div>
      </div>
    </FormModal>
  );
};

export default VenueWebsiteBuilderModal;
